import { CircularProgress, IconButton, Tooltip } from '@mui/material';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
} from '@mui/x-data-grid-pro';
import moment from 'moment';

import { IArrestToolRow } from '@/api/api.arrests';
import {
  IcDescriptionXml,
  IcPencil,
  IcPrint,
  IcVisibility,
} from '@/assets/images';
import { excludeValues } from '@/components/DataGrid/utils';
import { DATE_FORMAT } from '@/config';
import {
  DATA_WORKS_STATUS_MAP,
  isDataWorksExportEnabled,
} from '@/constants/arrest';
import { IOffenseOption, IOffenseOptionItem } from '@/models';
import { getOffenseOptionItems } from '@/pages/Kases/KaseCrimes/utilities';

const WAS_ARRESTED_TEXT: Record<number, string> = {
  2: 'Arrested',
  1: 'Pending',
};

const arrestId = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.arrestId || '-',
  valueGetter: (_, row) => row.arrestId || '',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const arresteeName = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.fullName || '-',
  valueGetter: (_, row) => row.fullName || '',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const arrestDate = (): Partial<GridColDef> => ({
  filterOperators: getGridDateOperators(true),
  renderCell: (params) =>
    params.row.arrestDate
      ? moment(params.row.arrestDate).format(DATE_FORMAT)
      : '-',
  valueGetter: (_, row) => row.arrestDate,
});

const arrestingOfficerName = (): Partial<GridColDef> => ({
  renderCell: (params) => params.row.arrestingOfficer?.fullName || '-',
  valueGetter: (_, row) => row.arrestingOfficer?.fullName || '',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const arrestType = (
  filteredOptions?: IOffenseOption[],
): Partial<GridColDef> => {
  const ArrestTypeLabel = getOffenseOptionItems(
    'arrestType',
    filteredOptions ?? [],
  ) as Map<string, IOffenseOptionItem>;
  return {
    filterable: false,
    renderCell: (params) =>
      (!!ArrestTypeLabel.size &&
        ArrestTypeLabel.get(params.row.arrestType)?.label) ||
      '-',
    valueGetter: (_, row) =>
      (!!ArrestTypeLabel.size && ArrestTypeLabel.get(row.arrestType)?.label) ||
      '',
  };
};

// Render-only; backed by a $lookup on the backend so sort/filter would cost a pre-pagination join.
const dataWorksStatus = (): Partial<GridColDef> => ({
  filterable: false,
  sortable: false,
  renderCell: (params) => {
    const s = params.row.dataWorksStatus;
    return s ? DATA_WORKS_STATUS_MAP[s] ?? s : '-';
  },
});

const arresteeStatus = (
  filteredOptions?: IOffenseOption[],
): Partial<GridColDef> => {
  const ArresteeStatusLabel = getOffenseOptionItems(
    'arrestStatus',
    filteredOptions ?? [],
  ) as Map<string, IOffenseOptionItem>;
  return {
    renderCell: (params) =>
      (ArresteeStatusLabel.size &&
        ArresteeStatusLabel.get(params.row.arrestStatus)?.label) ||
      '-',
    valueGetter: (_, row) =>
      ArresteeStatusLabel.get(row.arrestStatus)?.label ?? '',
    filterOperators: getGridStringOperators().filter(
      excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
    ),
  };
};

const caseId = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const kase = params.row.kase;
    if (!kase?._id || !kase?.number) return '-';
    return (
      <a
        href={`/cases/${kase._id}/overview`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`Open case ${kase.number} in new tab`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
        }}
        className="underline text-primary hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
      >
        {kase.number}
      </a>
    );
  },
  valueGetter: (_, row) => row.kase?.number ?? '',
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const arrestStatus = (): Partial<GridColDef> => ({
  filterable: false,
  sortable: false,
  renderCell: (params) => WAS_ARRESTED_TEXT[params.row.wasArrested] ?? '-',
});

const actions = (
  onView?: (row: IArrestToolRow) => void,
  onEdit?: (row: IArrestToolRow) => void,
  onPrint?: (row: IArrestToolRow) => void,
  onExport?: (row: IArrestToolRow) => void,
  isPrinting?: boolean,
  printingArresteeId?: string | null,
  isReadOnly?: boolean,
): Partial<GridColDef> => ({
  sortable: false,
  filterable: false,
  renderCell: (params) => {
    const row = params.row as IArrestToolRow;
    const exportEnabled = isDataWorksExportEnabled({
      arrestDate: row.arrestDate,
      kaseNumber: row.kase?.number,
      dataWorksStatus: row.dataWorksStatus,
    });

    let exportTooltip = 'Export to DataWorks';
    if (row.dataWorksStatus) exportTooltip = 'Export already initiated';
    else if (!row.arrestDate) exportTooltip = 'Arrest date is required';

    return (
      <div className="w-full">
        <IconButton
          aria-label="view"
          onClick={(e) => {
            e.stopPropagation();
            onView?.(row);
          }}
        >
          <IcVisibility />
        </IconButton>
        {!isReadOnly && (
          <IconButton
            aria-label="edit"
            onClick={(e) => {
              e.stopPropagation();
              onEdit?.(row);
            }}
          >
            <IcPencil />
          </IconButton>
        )}
        {!isReadOnly && (
          <IconButton
            aria-label="print"
            disabled={!!isPrinting}
            onClick={(e) => {
              e.stopPropagation();
              onPrint?.(row);
            }}
          >
            {isPrinting && printingArresteeId === row?._id ? (
              <CircularProgress size={18} />
            ) : (
              <IcPrint />
            )}
          </IconButton>
        )}
        <Tooltip title={exportTooltip}>
          <span>
            <IconButton
              aria-label="export-to-dataworks"
              disabled={!exportEnabled}
              onClick={(e) => {
                e.stopPropagation();
                onExport?.(row);
              }}
              sx={{ '&.Mui-disabled': { opacity: 0.4 } }}
            >
              <IcDescriptionXml />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    );
  },
});

export const arrestsToolCols = (
  _navigate?: (path: string) => void, // kept for signature compat; no longer used
  filteredOptions?: IOffenseOption[],
  onPrint?: (row: IArrestToolRow) => void,
  isPrinting?: boolean,
  printingArresteeId?: string | null,
  onView?: (row: IArrestToolRow) => void,
  onEdit?: (row: IArrestToolRow) => void,
  onExport?: (row: IArrestToolRow) => void,
  isReadOnly?: boolean,
) => ({
  arrestId,
  arresteeName,
  arrestDate,
  arrestingOfficerName,
  dataWorksStatus,
  arrestType: () => arrestType(filteredOptions),
  arresteeStatus: () => arresteeStatus(filteredOptions),
  caseId,
  arrestStatus,
  actions: () =>
    actions(
      onView,
      onEdit,
      onPrint,
      onExport,
      isPrinting,
      printingArresteeId,
      isReadOnly,
    ),
});
